/* eslint-disable react/prop-types */
/* eslint-disable react-refresh/only-export-components */

/**
 * PropertyPhotoGallery — Photo gallery for Commercial PDP.
 * Displays property photos in a featured grid layout with the first photo
 * enlarged, category overlay labels, a "+N More" indicator, and a "View All"
 * button that opens a full-screen PhotoSwipe modal with a thumbnail strip.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Gallery, Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import './property-photo-gallery.css';

/**
 * Determines how many photos to show in the grid.
 * Desktop: featured layout with 5 or 9 visible.
 * Mobile: 2-col grid with max 6 visible (3 rows).
 */
const getVisibleCount = (total, isMobile = false) => {
  if (isMobile) {
    if (total <= 6) return total;
    return 6;
  }
  if (total < 6) return total;
  if (total < 9) return 5;
  return 9;
};

/**
 * Normalizes a URL by handling protocol-relative URLs.
 * @param {string|null|undefined} url
 * @returns {string}
 */
export const normalizeUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return url;
};

/**
 * Filters out photos with invalid pdpBigImage values.
 * @param {Array} imageDetails
 * @returns {Array}
 */
export const filterValidPhotos = (imageDetails) => {
  if (!Array.isArray(imageDetails)) return [];
  return imageDetails.filter(
    (photo) =>
      photo &&
      typeof photo.pdpBigImage === 'string' &&
      photo.pdpBigImage.trim().length > 0
  );
};

/**
 * Groups valid photos by category, preserving first-appearance order.
 * Photos with null/empty category go into "Other" group last.
 * @param {Array} validPhotos
 * @returns {Array<{category: string, photos: Array}>}
 */
export const groupPhotosByCategory = (validPhotos) => {
  if (!Array.isArray(validPhotos) || validPhotos.length === 0) return [];

  const categoryMap = new Map();
  const otherPhotos = [];

  for (const photo of validPhotos) {
    const category = photo.category;
    const isEmptyCategory =
      category === null ||
      category === undefined ||
      (typeof category === 'string' && category.trim().length === 0);

    if (isEmptyCategory) {
      otherPhotos.push(photo);
    } else {
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category).push(photo);
    }
  }

  const groups = [];
  for (const [category, photos] of categoryMap) {
    groups.push({ category, photos });
  }
  if (otherPhotos.length > 0) {
    groups.push({ category: 'Other', photos: otherPhotos });
  }
  return groups;
};

/**
 * Builds and injects a thumbnail strip + category label into the PhotoSwipe lightbox.
 * Called via Gallery's onBeforeOpen prop.
 */
const buildThumbnailStrip = (pswp, photos) => {
  // Create the strip container
  const strip = document.createElement('div');
  strip.className = 'pswp-thumbnail-strip';

  const inner = document.createElement('div');
  inner.className = 'pswp-thumbnail-strip__inner';
  strip.appendChild(inner);

  // Create one thumbnail per photo
  photos.forEach((photo, idx) => {
    const thumb = document.createElement('button');
    thumb.className = 'pswp-thumbnail-strip__thumb';
    thumb.setAttribute('aria-label', photo.imageDescription || `Photo ${idx + 1}`);
    thumb.setAttribute('type', 'button');

    const img = document.createElement('img');
    img.src = normalizeUrl(photo.thumbnailUrl || photo.pdpBigImage);
    img.alt = photo.imageDescription || `Photo ${idx + 1}`;
    img.draggable = false;

    thumb.appendChild(img);
    thumb.addEventListener('click', () => {
      pswp.goTo(idx);
    });

    inner.appendChild(thumb);
  });

  // Sync active state on slide change
  const syncActive = () => {
    const currentIndex = pswp.currIndex;
    const thumbs = inner.querySelectorAll('.pswp-thumbnail-strip__thumb');
    thumbs.forEach((t, i) => {
      t.classList.toggle('pswp-thumbnail-strip__thumb--active', i === currentIndex);
    });
    // Scroll active thumb into view
    const activeThumb = thumbs[currentIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  pswp.on('change', syncActive);
  pswp.on('afterInit', syncActive);

  // Inject strip into PhotoSwipe root
  pswp.on('uiRegister', () => {
    pswp.ui.registerElement({
      name: 'thumbnail-strip',
      order: 9,
      isButton: false,
      appendTo: 'root',
      html: '',
      onInit: (el) => {
        el.appendChild(strip);
      },
    });

    // Inject image title next to the counter in the header
    pswp.ui.registerElement({
      name: 'category-label',
      order: 5,
      isButton: false,
      appendTo: 'bar',
      html: '<span class="pswp__category-label"></span>',
      onInit: (el) => {
        const labelEl = el.querySelector('.pswp__category-label');
        const updateTitle = () => {
          const photo = photos[pswp.currIndex];
          const title = photo?.imageTitle || '';
          labelEl.textContent = title;
          labelEl.style.display = title ? '' : 'none';
        };
        pswp.on('change', updateTitle);
        pswp.on('afterInit', updateTitle);
      },
    });
  });
};

/**
 * PhotoThumbnail — single photo card with category overlay and error fallback.
 */
const PhotoThumbnail = ({ photo, index, isFeatured, isLast, remainingCount, photoSwipeRef, openModal, onImageError }) => {
  const [hasError, setHasError] = useState(false);
  const thumbnailSrc = normalizeUrl(photo.pdpBigImage || photo.thumbnailUrl);
  const altText = photo.imageDescription || `Property photo ${index + 1}`;
  const categoryLabel = photo.category || '';
  const imageTitle = photo.imageTitle || '';

  return (
    <div
      className={`property-photo-gallery__item${isFeatured ? ' property-photo-gallery__item--featured' : ''}`}
      onClick={openModal}
      ref={photoSwipeRef}
      role="button"
      tabIndex={0}
      aria-label={altText}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal();
        }
      }}
    >
      {(!thumbnailSrc || hasError) ? (
        <img
          className="property-photo-gallery__thumbnail"
          src="/assets/images/imageOnTheWay.webp"
          alt="Image on the way"
        />
      ) : (
        <img
          className="property-photo-gallery__thumbnail"
          src={thumbnailSrc}
          alt={altText}
          onError={() => {
            setHasError(true);
            if (onImageError) onImageError();
          }}
        />
      )}

      {index === 0 && (
        <span className="property-photo-gallery__badge">Featured</span>
      )}

      {categoryLabel && !imageTitle && (
        <div className={`property-photo-gallery__category-overlay${isFeatured ? ' property-photo-gallery__category-overlay--featured' : ''}`}>
          <p className={`property-photo-gallery__category-text${isFeatured ? ' property-photo-gallery__category-text--featured' : ''}`}>
            {categoryLabel}
          </p>
        </div>
      )}

      <div className={`property-photo-gallery__title-overlay${isFeatured ? ' property-photo-gallery__title-overlay--featured' : ''}${!imageTitle ? ' property-photo-gallery__title-overlay--empty' : ''}`}>
        <p className={`property-photo-gallery__title-text${isFeatured ? ' property-photo-gallery__title-text--featured' : ''}`}>
          {imageTitle}
        </p>
      </div>


      {isLast && remainingCount > 0 && (
        <div className="property-photo-gallery__more-overlay">
          <span className="property-photo-gallery__more-text">+{remainingCount} More</span>
        </div>
      )}
    </div>
  );
};

/** Photo gallery icon for the header photo count */
const PhotoIcon = () => (
  <img
    className="property-photo-gallery__count-icon"
    src="/assets/images/pdp/photogallery.svg"
    alt=""
    aria-hidden="true"
  />
);

const PropertyPhotoGallery = ({ imageDetails }) => {
  const validPhotos = filterValidPhotos(imageDetails);
  const openFunctionsRef = useRef([]);
  const [failedCount, setFailedCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageError = useCallback(() => {
    setFailedCount((prev) => prev + 1);
  }, []);

  // Inject thumbnail strip when PhotoSwipe opens
  const handleBeforeOpen = useCallback((pswp) => {
    buildThumbnailStrip(pswp, validPhotos);
  }, [validPhotos]);

  if (validPhotos.length === 0) {
    return (
      <div className="property-photo-gallery">
        <div className="property-photo-gallery__header">
          <h2 className="property-photo-gallery__title">Property Photos</h2>
        </div>
        <div className="property-photo-gallery__grid">
          <div className="property-photo-gallery__item property-photo-gallery__item--featured">
            <img
              className="property-photo-gallery__thumbnail"
              src="/assets/images/imageOnTheWay.webp"
              alt="Image on the way"
            />
          </div>
        </div>
      </div>
    );
  }

  const totalCount = validPhotos.length;
  const allFailed = failedCount >= totalCount;
  const visibleCount = getVisibleCount(totalCount, isMobile);
  const visiblePhotos = validPhotos.slice(0, visibleCount);
  const remainingCount = totalCount - visibleCount;
  const isSimpleLayout = !isMobile && totalCount <= 3;

  const buttonText = totalCount === 1 ? 'View 1 Photo' : `View All ${totalCount} Photos`;

  const handleViewAllClick = () => {
    if (openFunctionsRef.current[0]) openFunctionsRef.current[0]();
  };

  return (
    <div className="property-photo-gallery">
      {/* Section Header */}
      <div className="property-photo-gallery__header">
        <h2 className="property-photo-gallery__title">Property Photos</h2>
        {!allFailed && (
          <div className="property-photo-gallery__count">
            <PhotoIcon />
            <span>{totalCount} {totalCount === 1 ? 'Photo' : 'Photos'}</span>
          </div>
        )}
      </div>

      {/* Photo Grid + PhotoSwipe */}
      <Gallery onBeforeOpen={handleBeforeOpen} options={{ zoom: false }}>
        <div className={`property-photo-gallery__grid${isSimpleLayout ? ' property-photo-gallery__grid--simple' : ''}`}>
          {visiblePhotos.map((photo, index) => (
            <Item
              key={index}
              original={normalizeUrl(photo.pdpBigImage)}
              thumbnail={normalizeUrl(photo.thumbnailUrl || photo.pdpBigImage)}
              width={1200}
              height={800}
            >
              {({ ref, open }) => {
                openFunctionsRef.current[index] = open;
                return (
                  <PhotoThumbnail
                    photo={photo}
                    index={index}
                    isFeatured={!isSimpleLayout && index === 0}
                    isLast={index === visiblePhotos.length - 1}
                    remainingCount={remainingCount}
                    photoSwipeRef={ref}
                    openModal={open}
                    onImageError={handleImageError}
                  />
                );
              }}
            </Item>
          ))}
        </div>

        {/* Hidden items for photos beyond visible count — still navigable in modal */}
        {validPhotos.slice(visibleCount).map((photo, idx) => {
          const actualIndex = visibleCount + idx;
          return (
            <Item
              key={actualIndex}
              original={normalizeUrl(photo.pdpBigImage)}
              thumbnail={normalizeUrl(photo.thumbnailUrl || photo.pdpBigImage)}
              width={1200}
              height={800}
            >
              {({ ref, open }) => {
                openFunctionsRef.current[actualIndex] = open;
                return <span ref={ref} style={{ display: 'none' }} onClick={open} aria-hidden="true" />;
              }}
            </Item>
          );
        })}
      </Gallery>

      {/* View All Button — hidden when all images failed to load */}
      {!allFailed && (
        <div className="property-photo-gallery__view-all-wrapper">
          <button className="property-photo-gallery__view-all" onClick={handleViewAllClick} type="button">
            {buttonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyPhotoGallery;
